import { inject, NetworkException } from "fw";
import { Store, handle } from "fw-state";
import { LogoutAction, StartAction } from "./actions";
import { once } from "helpers/once";
import { AtsRepository } from "network/ats-repository";
import { AtsImportFilter, AtsImportTaskInfo, ImportStatuses } from "models/monitor-application-imports";
import { CurrentUserStore } from "./current-user";
import { Filter, OrganizationFilter } from "models/monitor-exports";
import { LocalStorageCache } from "caching";
import { NestedAggregationItem, StatusAggregationItem } from "models/aggregations";
import { AppSyncFilter } from "models/monitor-appsync-results";
import { quoteIfSpecialCharacters } from "helpers/filter";
import { AccountType } from "models/ats-organization";

const REQUIRED_GRID_FIELDS = "*";
const DEFAULT_PAGE_SIZE = 20;
const DEFAULT_PAGE_NUMBER = 1;

const AGGREGATIONS =
    'terms:status  date:(metaData.dateCreatedUtc~DATEINTERVAL^"TIMEZONE" terms:status terms:metaData.organizationId~20 avg:metaData.duration avg:metaData.processingDelay avg:metaData.durationPerRecord)';

const FILTER_FIELDS_MAPPING: Map<string, string> = new Map()
    .set("organization", "metaData.organizationId")
    .set("source", "metaData.applicationSourceId")
    .set("status", "status")
    .set("range", "metaData.dateCreatedUtc")
    .set("origin", "metaData.origin")
    .set("originEnvironment", "metaData.originEnvironment")
    .set("originTag", "metaData.originTags");

const CACHE_KEY = "ats-monitor-applications-store";

export type SelectionItem = {
    label: string;
    value: number;
    color?: string;
    hexColor?: string;
};

export class ImportsEnsureAction {}
export class PreviousPageAction {}
export class NextPageAction {}
export class CollapseItemAction {
    constructor(public id: string) {}
}

export class RetryApplicationImport {
    constructor(public applicationSourceHistoryId: string) {}
}

export class SelectedFilterAction {
    constructor(
        public key: number,
        public data: any
    ) {}
}
export class RefreshAction {}
export class setDefaultStateAction {}

export class FetchListAction<Filter> {
    constructor(
        public filter: Filter = null,
        public search: string = null,
        public orgFilter: OrganizationFilter = {},
        public sort: string = ""
    ) {}
}

type SavedMonitorApplicationGridState<Filter> = {
    filter: Filter;
    filterCounter: number;
    search: string;
    sort: string;
    pageNumber: number;
    pageSize: number;
    previousPageToken: string;
    nextPageToken: string;
    currentPreviousPageToken: string;
    currentNextPageToken: string;
    orgFilter: OrganizationFilter;
    runDateAggInterval: string;
};
export type MonitorApplication<List, Filter> = {
    currentUserId: string;

    loaded: boolean;
    collapsedLoaded: boolean;
    errorLoading: boolean;
    errorMessage: string;
    list: List[];
    statusCounts: StatusAggregationItem[];
    dateBuckets: NestedAggregationItem[];
    total: number;
    filter: Filter;
    filterCounter: number;
    selectedFilter: any;
    search: string;
    sort: string;
    runDateAggInterval: string;

    previousPageToken: string;
    nextPageToken: string;
    currentPreviousPageToken: string;
    currentNextPageToken: string;
    pageSize: number;
    pageNumber: number;

    collapsedItem: string;
    updatedDate: Date;
    orgFilter: OrganizationFilter;
};

export class MonitorApplicationStore<List, Filter> extends Store<MonitorApplication<List, Filter>> {
    protected defaultState(): MonitorApplication<List, Filter> {
        return {
            currentUserId: null,

            loaded: false,
            collapsedLoaded: false,
            errorLoading: false,
            errorMessage: "",
            list: [],
            statusCounts: [],
            dateBuckets: [],
            total: 0,
            filter: {} as Filter,
            filterCounter: 0,
            selectedFilter: {},
            search: null,
            sort: null,
            runDateAggInterval: "1d",
            previousPageToken: null,
            nextPageToken: null,
            currentPreviousPageToken: null,
            currentNextPageToken: null,
            pageSize: DEFAULT_PAGE_SIZE,
            pageNumber: DEFAULT_PAGE_NUMBER,

            collapsedItem: "",
            updatedDate: new Date(),
            orgFilter: { type: AccountType.Customer, tags: null }
        };
    }
}

@inject
export class MonitorApplicationImportsStore extends MonitorApplicationStore<AtsImportTaskInfo, AtsImportFilter> {
    private rerunningAggs: boolean = false;
    private cache: LocalStorageCache = new LocalStorageCache();
    private readonly keyPostfix;
    private lastFilter: string = null;
    private lastOrgFilter: string = null;

    constructor(
        private network: AtsRepository,
        private currentUserStore: CurrentUserStore
    ) {
        super();
    }

    public handleStartAction(action: StartAction): void {
        this.setState(() => ({
            ...this.defaultState(),
            currentUserId: action.user.Id
        }));
    }

    public handleCollapseItemAction(action: CollapseItemAction, callback = null): Function {
        this.setState((state) => ({
            ...state,
            collapsedItem: action.id,
            collapsedLoaded: false
        }));

        if (callback) return callback();
    }

    public handleSelectedFilter(action: SelectedFilterAction) {
        this.setState((state) => ({
            ...state,
            selectedFilter: {
                ...state.selectedFilter,
                [action.key]: action.data
            }
        }));
    }

    public handleLogoutAction(): void {
        this.setState(() => this.defaultState());
    }

    @handle(StartAction)
    private handleActionStart(action: StartAction) {
        this.handleStartAction(action);
    }

    @handle(CollapseItemAction)
    private collapseItem(action: CollapseItemAction) {
        this.handleCollapseItemAction(action, () => {
            if (action.id) {
                this.loadAdditionalInfo(action.id);
            }
        });
    }

    @handle(SelectedFilterAction)
    private handleSelectedFilterAction(action: SelectedFilterAction) {
        this.handleSelectedFilter(action);
    }
    @handle(RefreshAction)
    private refreshAction(action: RefreshAction) {
        this.handleRefreshAction(action, () => {
            this.loadPageList();
        });
    }

    @handle(NextPageAction)
    private nextPageAction(action: NextPageAction) {
        this.handleNextPageAction(action, async (nextPageToken) => {
            await this.loadPageData(null, nextPageToken);
        });
    }

    @handle(PreviousPageAction)
    private previousPageAction(action: NextPageAction) {
        this.handlePreviousPage(action, async (previousPageToken) => {
            await this.loadPageData(previousPageToken, null);
        });
    }
    @handle(LogoutAction)
    private logoutAction() {
        this.handleLogoutAction();
    }

    @handle(ImportsEnsureAction)
    private async handleAtsOrganizationEnsureAction() {
        await once("ensure-monitor-applications", async () => {
            if (this.state.loaded) {
                return;
            }

            this.restoreState();
        });
    }

    @handle(RetryApplicationImport)
    private async retryApplicationImport(action: RetryApplicationImport) {
        return await this.network.atsRetryImport(action.applicationSourceHistoryId);
    }

    @handle(FetchListAction)
    private async fetchListAction(action: FetchListAction<Filter>) {
        await this.handleFetchList(action, async () => {
            await this.loadPageList();
        });
    }

    private async loadAdditionalInfo(id: string) {
        try {
            const history = await this.network.atsExtendedImportHistoryById(id);
            if (history) {
                let log = null;
                if (history.EphemeralLogFileUrl) {
                    log = await (await fetch(history.EphemeralLogFileUrl)).text();
                }

                this.setState((state) => ({
                    ...state,
                    list: this.state.list.map((task) => {
                        return task.History.Id === history.Id
                            ? {
                                  ...task,
                                  Extended: { ...history, log }
                              }
                            : task;
                    })
                }));
            }
        } catch (err) {
            this.setState((state) => ({ ...state, errorLoading: true }));
        }
    }

    restoreState() {
        if (this.state.currentUserId == null) return;

        const savedState = this.cache.get<SavedMonitorApplicationGridState<Filter>>(this.cacheKey);

        if (savedState) {
            this.setState((state) => ({
                ...state,
                filter: savedState.filter,
                search: savedState.search,
                sort: savedState.sort,
                pageNumber: savedState.pageNumber || 1,
                runDateAggInterval: savedState.runDateAggInterval || "1h",
                pageSize: savedState.pageSize || DEFAULT_PAGE_SIZE,
                previousPageToken: savedState.previousPageToken,
                nextPageToken: savedState.nextPageToken,
                currentPreviousPageToken: savedState.currentPreviousPageToken,
                currentNextPageToken: savedState.currentNextPageToken,
                orgFilter: savedState.orgFilter
            }));
        }
    }

    public handleRefreshAction(action: RefreshAction, callback = null): Function {
        if (!this.state?.loaded) return;

        this.setState((state) => ({
            ...state,
            loaded: false,
            statusCounts: []
        }));

        this.saveState();

        if (callback) return callback();
    }

    public saveState(): void {
        if (this.state.currentUserId == null) return;

        const { list, loaded, statusCounts, total, orgFilter, ...rest } = this.state;

        this.cache?.set<SavedMonitorApplicationGridState<Filter>>(this.cacheKey, {
            ...rest,
            orgFilter: this.state.orgFilter
        });
    }

    get cacheKey(): string {
        return `manager-user:${this.state.currentUserId}:${this.keyPostfix}`;
    }

    public handleFetchList(action: FetchListAction<Filter>, callback = null): Function {
        this.setState(() => ({
            ...this.state,
            filter: action.filter,
            search: action.search,
            sort: action.sort,
            loaded: false,
            collapsedLoaded: false,
            collapsedItem: "",
            errorLoading: false,
            errorMessage: "",
            pageNumber: DEFAULT_PAGE_NUMBER,
            previousPageToken: null,
            nextPageToken: null,
            currentPreviousPageToken: null,
            currentNextPageToken: null,
            orgFilter: action.orgFilter
        }));

        this.saveState();
        if (callback) return callback();
    }

    public async handleNextPageAction(action: NextPageAction, callback): Promise<Function> {
        const { nextPageToken, pageSize, pageNumber, total } = this.state;

        if (pageNumber * pageSize > total) return;

        this.setState((state) => ({
            ...state,
            loaded: false,
            collapsedItem: "",
            pageNumber: pageNumber + 1
        }));

        return callback(nextPageToken);
    }

    public async handlePreviousPage(action: PreviousPageAction, callback): Promise<Function> {
        const { previousPageToken, pageNumber } = this.state;
        if (pageNumber <= 1) return;

        this.setState((state) => ({
            ...state,
            loaded: false,
            collapsedItem: "",
            pageNumber: pageNumber - 1
        }));

        return callback(previousPageToken);
    }

    private async loadPageList() {
        await this.loadPageData(this.state?.currentPreviousPageToken, this.state?.currentNextPageToken);
    }

    private async loadPageData(previousPageToken: string, nextPageToken: string) {
        const { pageSize, sort, search, filter, statusCounts, filterCounter } = this.state;
        let { runDateAggInterval } = this.state;
        const filterLength = filter ? Object.keys(filter).length : 0;
        let newStatusCounts = [];
        let newDateBuckets = [];
        const fieldPaths = `PreviousPageToken,NextPageToken,Total,Results(id,${REQUIRED_GRID_FIELDS})`;

        if (!this.rerunningAggs && !filter.range) runDateAggInterval = "1M";

        try {
            const filter = this.getWholeFilter(false, FILTER_FIELDS_MAPPING);
            const orgFilter = this.getOrgFilter();

            const clientModelResponse = await this.network.atsApplicationsList(
                this.getWholeFilter(true, FILTER_FIELDS_MAPPING),
                search,
                sort,
                previousPageToken,
                nextPageToken,
                fieldPaths,
                AGGREGATIONS.replace("DATEINTERVAL", runDateAggInterval).replace("TIMEZONE", this.currentUserStore.state.timezone),
                false,
                this.getOrgFilter(),
                pageSize
            );

            if (!statusCounts || !statusCounts.length || (!filter && !orgFilter) || filter !== this.lastFilter || orgFilter !== this.lastOrgFilter) {
                const clientModelResponseAggs = await this.network.atsApplicationsList(
                    filter,
                    search,
                    sort,
                    previousPageToken,
                    nextPageToken,
                    fieldPaths,
                    AGGREGATIONS.replace("DATEINTERVAL", runDateAggInterval).replace("TIMEZONE", this.currentUserStore.state.timezone),
                    true,
                    orgFilter,
                    pageSize
                );
                if (clientModelResponseAggs?.Aggregations) {
                    newStatusCounts = clientModelResponseAggs?.Aggregations["terms_status"]?.Items;
                    newDateBuckets = clientModelResponseAggs?.Aggregations["date_metaData.dateCreatedUtc"]?.Items;
                }
            } else {
                newStatusCounts = statusCounts;
                newDateBuckets = clientModelResponse?.Aggregations["date_metaData.dateCreatedUtc"]?.Items;
            }

            if (runDateAggInterval == "1d" && newDateBuckets?.length > 40) {
                this.state.runDateAggInterval = "1M";
                this.rerunningAggs = true;
                await this.loadPageData(previousPageToken, nextPageToken);
                this.rerunningAggs = false;
                return;
            } else if (runDateAggInterval == "1d" && newDateBuckets?.length == 1) {
                this.state.runDateAggInterval = "1h";
                this.rerunningAggs = true;
                await this.loadPageData(previousPageToken, nextPageToken);
                this.rerunningAggs = false;
                return;
            } else if (runDateAggInterval == "1M" && newDateBuckets?.length == 1) {
                this.state.runDateAggInterval = "1d";
                this.rerunningAggs = true;
                await this.loadPageData(previousPageToken, nextPageToken);
                this.rerunningAggs = false;
                return;
            } else if (runDateAggInterval == "1h" && newDateBuckets?.length > 24) {
                this.state.runDateAggInterval = "1d";
                this.rerunningAggs = true;
                await this.loadPageData(previousPageToken, nextPageToken);
                this.rerunningAggs = false;
                return;
            }

            this.lastFilter = filter;
            this.lastOrgFilter = orgFilter;

            this.setState((state) => ({
                ...state,
                loaded: true,
                filterCounter: filterLength,
                statusCounts: newStatusCounts ?? [],
                previousPageToken: clientModelResponse.PreviousPageToken,
                nextPageToken: clientModelResponse.NextPageToken,
                currentPreviousPageToken: previousPageToken,
                currentNextPageToken: nextPageToken,
                list: clientModelResponse.Results,
                total: clientModelResponse.Total,
                dateBuckets: newDateBuckets,
                runDateAggInterval: runDateAggInterval,
                updatedDate: new Date(),
                orgFilter: state.orgFilter
            }));

            this.saveState();
        } catch (err) {
            let message = "Error loading application import data";
            if (err instanceof NetworkException && err.result?.message) {
                message += `: ${err.result.message}`;
            }
            this.setState((state) => ({ ...state, loaded: true, errorLoading: true, errorMessage: message }));
        }
    }

    public getWholeFilter(withStatus = true, mapping: Map<string, string> = new Map()): string {
        const { filter } = this.state;
        if (!filter || !Object.keys(filter).length) return null;

        const filterArgs = [];
        for (const [key, value] of Object.entries(filter)) {
            if (value === undefined || value === null) continue;

            if (!withStatus && key === "status") continue;

            if (Array.isArray(value) && value.length) {
                (value as Array<string>).forEach((element) => {
                    filterArgs.push(`${mapping.get(key)}:${element}`);
                });
            } else if (key === "originTag") {
                filterArgs.push(`${mapping.get(key)}:${quoteIfSpecialCharacters(String(value))}`);
            } else {
                filterArgs.push(`${mapping.get(key)}:${value}`);
            }
        }

        return filterArgs.join(" AND ");
    }
    private getOrgFilter(): string {
        const { tags, type } = this.state.orgFilter;

        if (type === null && !tags?.length) return null;

        const filterArgs = [];
        if (type !== null) filterArgs.push(`metaData.type:${type}`);
        if (tags?.length) filterArgs.push(...tags.map((tag) => `tags:("${tag}")`));

        return filterArgs.join(" AND ");
    }
}
